import { useGoBack } from 'hooks/useGoBack';
import React, { useCallback, useContext } from 'react';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { convertUndefinedFields } from 'utils/helpers';
import ModalSentErrorWithInfo from './ModalSentErrorWithInfo/ModalSentErrorWithInfo';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditModifierContext, IEditModifierContext } from '../../editModifierState/editModifierContext';
import { updateModifier } from 'backend/modifiers';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		refetchData,
		showErrorModalWithInfo,
		errorResponseData,
		dataForSubmit,
		typeOfModalWindow,
		fetchedRestaurants,
		fetchedModifierGroups,
	} = useContext<IEditModifierContext>(EditModifierContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow || errorResponseData });
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/menu/modifiers');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModifier(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.MODIFIER_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfo
				onConsentEvent={handleCloseModalWithInfo}
				isVisible={!!errorResponseData}
				errorResponseData={errorResponseData}
				fetchedRestaurants={fetchedRestaurants}
				fetchedModifierGroups={fetchedModifierGroups}
			/>
		</div>
	);
};

export default ModalWindows;
